import React, { useState, useRef, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import PatternClusterCard from './PatternClusterCard';
import { Pattern } from '../types';

interface SwipeablePatternCardProps {
  pattern: Pattern;
  translations: {
    timesObserved: string;
    connectedTo: string;
    insight: string;
    typeLabels: {
      emotional: string;
      behavioral: string;
      temporal: string;
      relational: string;
      decision: string;
    };
  };
  onFeedback: (patternId: string, patternTitle: string, feedbackType: 'helpful' | 'not_helpful') => void;
  language?: string;
}

const LABELS = {
  Italian: { helpful: 'Utile', notHelpful: 'Non utile', thanks: 'Grazie!', cancel: 'Annulla' },
  English: { helpful: 'Useful', notHelpful: 'Not useful', thanks: 'Thanks!', cancel: 'Cancel' },
  Spanish: { helpful: 'Útil', notHelpful: 'No útil', thanks: '¡Gracias!', cancel: 'Cancelar' },
  German: { helpful: 'Hilfreich', notHelpful: 'Nicht hilfreich', thanks: 'Danke!', cancel: 'Abbrechen' },
  French: { helpful: 'Utile', notHelpful: 'Pas utile', thanks: 'Merci!', cancel: 'Annuler' }
};

const SwipeablePatternCard: React.FC<SwipeablePatternCardProps> = ({
  pattern,
  translations,
  onFeedback,
  language = 'English'
}) => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<'helpful' | 'not_helpful' | null>(null);
  const [showActions, setShowActions] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const startXRef = useRef(0);
  const velocityRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);

  const labels = LABELS[language as keyof typeof LABELS] || LABELS.English;
  const SWIPE_THRESHOLD = 70;
  const VELOCITY_THRESHOLD = 0.5;

  const triggerHaptic = useCallback((intensity: number = 10) => {
    if (navigator.vibrate) {
      navigator.vibrate(intensity);
    }
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (feedbackGiven || isRemoving) return;
    startXRef.current = e.touches[0].clientX;
    lastXRef.current = e.touches[0].clientX;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;
    setIsDragging(true);
  }, [feedbackGiven, isRemoving]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || feedbackGiven || isRemoving) return;

    const currentX = e.touches[0].clientX;
    const currentTime = Date.now();
    const diff = currentX - startXRef.current;

    // Calculate velocity
    const timeDelta = currentTime - lastTimeRef.current;
    if (timeDelta > 0) {
      velocityRef.current = (currentX - lastXRef.current) / timeDelta;
    }
    lastXRef.current = currentX;
    lastTimeRef.current = currentTime;

    // If actions are shown, allow right swipe to close
    if (showActions) {
      if (diff > 0) {
        const newTranslate = -130 + diff;
        setTranslateX(Math.min(newTranslate, 0));
      }
      return;
    }

    // Only allow left swipe
    if (diff < 0) {
      const resistance = Math.abs(diff) > SWIPE_THRESHOLD ? 0.3 : 1;
      const newTranslate = diff * resistance;
      setTranslateX(Math.max(newTranslate, -140));

      if (Math.abs(diff) >= SWIPE_THRESHOLD && Math.abs(translateX) < SWIPE_THRESHOLD) {
        triggerHaptic(15);
      }
    }
  }, [isDragging, feedbackGiven, isRemoving, showActions, translateX, triggerHaptic]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    const shouldTrigger = Math.abs(translateX) >= SWIPE_THRESHOLD ||
                          Math.abs(velocityRef.current) > VELOCITY_THRESHOLD;

    // If actions were shown and user swiped right to close
    if (showActions && (translateX > -80 || velocityRef.current > VELOCITY_THRESHOLD)) {
      setTranslateX(0);
      setShowActions(false);
      return;
    }

    if (shouldTrigger && translateX < 0) {
      setTranslateX(-130);
      setShowActions(true);
      triggerHaptic(20);
    } else {
      setTranslateX(0);
      setShowActions(false);
    }
  }, [translateX, showActions, triggerHaptic]);

  const handleFeedback = useCallback((type: 'helpful' | 'not_helpful') => {
    setFeedbackGiven(type);
    triggerHaptic(15);
    onFeedback(pattern.id, pattern.title, type);

    if (type === 'not_helpful') {
      setIsRemoving(true);
    }

    setTimeout(() => {
      setTranslateX(0);
      setShowActions(false);
    }, 150);
  }, [pattern.id, pattern.title, onFeedback, triggerHaptic]);

  const handleCancel = useCallback(() => {
    setTranslateX(0);
    setShowActions(false);
  }, []);

  if (isRemoving) {
    return (
      <div className="transition-all duration-300 ease-out opacity-0 max-h-0 overflow-hidden">
        <PatternClusterCard pattern={pattern} translations={translations} />
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[20px] md:rounded-[24px]">
      {/* Background action buttons */}
      <div className="absolute inset-0 flex items-stretch justify-end rounded-[20px] md:rounded-[24px] overflow-hidden">
        <button
          onClick={() => handleFeedback('helpful')}
          className="w-16 bg-emerald-500 flex items-center justify-center transition-all duration-200"
          style={{ opacity: showActions ? 1 : 0, transform: showActions ? 'scale(1)' : 'scale(0.8)' }}
        >
          <ThumbsUp size={20} className="text-white" />
        </button>
        <button
          onClick={() => handleFeedback('not_helpful')}
          className="w-16 bg-red-500 flex items-center justify-center transition-all duration-200"
          style={{ opacity: Math.abs(translateX) > 20 ? 1 : 0 }}
        >
          <ThumbsDown size={20} className="text-white" />
        </button>
      </div>

      {/* Main card wrapper */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`relative z-10 transition-transform ${
          isDragging ? 'duration-0' : 'duration-200 ease-out'
        }`}
        style={{ transform: `translateX(${translateX}px)` }}
      >
        <PatternClusterCard pattern={pattern} translations={translations} />

        {/* Feedback indicator overlay */}
        {feedbackGiven && (
          <div className={`absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold shadow-lg ${
            feedbackGiven === 'helpful'
              ? 'bg-emerald-500 text-white'
              : 'bg-red-500 text-white'
          }`}>
            {feedbackGiven === 'helpful' ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
            {labels.thanks}
          </div>
        )}

        {/* Swipe hint for mobile */}
        {!feedbackGiven && !showActions && translateX === 0 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-20 pointer-events-none lg:hidden">
            <span className="text-[9px] font-medium">←</span>
            <div className="w-0.5 h-8 bg-black/30 rounded-full" />
          </div>
        )}

        {/* Close hint when actions are shown */}
        {showActions && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-black/40 font-medium pointer-events-none lg:hidden animate-pulse">
            → {labels.cancel}
          </div>
        )}
      </div>

      {/* Desktop action overlay */}
      {showActions && (
        <div className="hidden lg:flex absolute inset-0 z-20 items-center justify-end gap-2 pr-4 bg-gradient-to-l from-white via-white/95 to-transparent rounded-[20px] md:rounded-[24px]">
          <button
            onClick={handleCancel}
            className="px-3 py-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-800 transition-colors"
          >
            {labels.cancel}
          </button>
          <button
            onClick={() => handleFeedback('helpful')}
            className="px-3 py-1.5 text-[11px] font-bold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-all flex items-center gap-1 shadow-sm"
          >
            <ThumbsUp size={12} />
            {labels.helpful}
          </button>
          <button
            onClick={() => handleFeedback('not_helpful')}
            className="px-3 py-1.5 text-[11px] font-bold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all flex items-center gap-1 shadow-sm"
          >
            <ThumbsDown size={12} />
            {labels.notHelpful}
          </button>
        </div>
      )}
    </div>
  );
};

export default SwipeablePatternCard;
